import { RJSFSchema } from "@rjsf/utils";
import { GenericObjectType } from "@rjsf/utils";
import type { JSONSchema7Definition } from "json-schema";
import moment from "moment";
import { useCallback, useContext, useMemo } from "react";
import { IntlShape, useIntl } from "react-intl";

import {
  ProcessTagFromJSON,
  ProcessTagSchemasFromJSON,
  instanceOfProcessTag,
} from "@nens/rana/process-types";
import { useRanaFormDerivedState } from "@nens/ranalib";
import { useRunOnceWhen } from "@nens/react/hooks";
import { camelToSnakeDeep, snakeToCamelDeep } from "@nens/shared-utils";

import { ProcessContext } from "@/lib/process-provider";
import { useJobListQuery } from "@/queries/query-job";
import { ProjectContext } from "@/stores/project-provider";

import {
  getSchemaDefaultValues,
  useJobResultsQuery,
  useProcessRunSchemas,
} from "../../project-process-run";
import { ProcessRunFormsProps } from "../process-run-form-collection";
import { ProcessRunFormData } from "../types/process-run-form-data";
import { customValidator } from "../utils/form-validator";
import { getDerivationRulesPerTag } from "../utils/process-derivation-rules";

const getNameSchema = (intl: IntlShape, hasAutoGeneratedName: boolean): RJSFSchema => ({
  properties: {
    name: {
      type: "string",
      title: intl.formatMessage({
        id: "process.run_name.title",
        defaultMessage: "Name of the process run",
      }),
      description: intl.formatMessage({
        id: "process.run_name.description",
        defaultMessage:
          "This name will determine how the process is saved and displayed in 'Analysis tools'.",
      }),
      ["ui:placeholder" as keyof RJSFSchema]: hasAutoGeneratedName
        ? intl.formatMessage({
            id: "process.run_name.auto_generated_placeholder",
            defaultMessage: "Name will be auto-generated based on inputs if left empty",
          })
        : undefined,
    },
  },
  required: ["name"],
  type: "object",
});

// Also checks if a required field is not equal to an empty string, since the customValidator considers empty strings valid for required fields
const checkRequiredForSchema = (
  schema: RJSFSchema | JSONSchema7Definition | undefined,
  formData: GenericObjectType
): boolean => {
  if (!schema || typeof schema === "boolean" || !("required" in schema) || !schema.required)
    return true;
  return Array.from(schema.required).every((requiredItem: string) => {
    if (!schema.properties) return true;
    const value = formData[requiredItem];
    return (
      (!(requiredItem in schema.properties) || (value !== undefined && value !== "")) &&
      checkRequiredForSchema(schema.properties[requiredItem], value)
    );
  });
};

export function useProcessRunFormCollection(props: ProcessRunFormsProps) {
  const intl = useIntl();
  const { process, job, setIsCreating, setJob, sourceJob, workflowId } = props;
  const project = useContext(ProjectContext);
  const { startRun, errors } = useContext(ProcessContext);

  // ========================================
  // 1. INITIAL STATE & SETUP
  // ========================================
  // Load process metadata (tags, derivation rules) and schemas from the backend.
  // Initialize form data from existing job or with empty/derived defaults.

  const processTag = instanceOfProcessTag(process.tags?.[0])
    ? ProcessTagFromJSON(process.tags?.[0])
    : undefined;
  const derivationRules = getDerivationRulesPerTag(processTag);
  const hasAutoGeneratedName = !!derivationRules?.name;

  const nameSchema = useMemo(
    () => getNameSchema(intl, hasAutoGeneratedName),
    [intl, hasAutoGeneratedName]
  );
  const { isLoading, inputSchema, outputSchema } = useProcessRunSchemas(props);
  const hasJob = !!job || !!sourceJob;

  const [formData, setFormData] = useRanaFormDerivedState<ProcessRunFormData<typeof processTag>>(
    {
      name: sourceJob?.name ?? job?.name ?? "",
      ...ProcessTagSchemasFromJSON({
        inputs: sourceJob?.inputs ?? job?.inputs ?? {},
        outputs: {},
        tag: processTag,
      }),
    },
    hasJob ? undefined : derivationRules // Only apply derivation rules if there is no job yet, otherwise we derive stuff from the job which we shouldn't do. The job is the source of truth for the form data, not the derivation rules.
  );

  // ========================================
  // 2. NAME FIELD MANAGEMENT
  // ========================================
  // Manage the process run name. Auto-generate with timestamp if no custom name is provided.

  const nameFormData = useMemo(() => ({ name: formData.name }), [formData.name]);
  const setNameFormData = useCallback(
    ({ name }: { name: string }) => {
      setFormData((prev) => ({ ...prev, name }));
    },
    [setFormData]
  );

  // Get jobs with the same process amount and add that name to the form data
  const jobQuery = useJobListQuery({
    processId: process.id,
    limit: 1,
    enabled: !job && !hasAutoGeneratedName,
    disableRefetching: true,
  });

  useRunOnceWhen(jobQuery.isSuccess, () => {
    setNameFormData({
      name: `${process?.name} #${(jobQuery.data?.total ?? 0) + 1} (${moment().format(
        "D MMM YYYY HH:mm"
      )})`,
    });
  });

  // ========================================
  // 3. FORM DATA CASE CONVERSION
  // ========================================
  // Convert between snake_case (API/form) and camelCase (derived rules/ProcessTag types). Necessary because:
  // - The API and form expects snake_case
  // - The derived rules and ProcessTag types use camelCase because the openAPI spec generates the schemas with camelCase.
  // We can't use the build in ToJSON and FromJSON functions for this because they have an `!== undefined` check for required fields
  // that does not work with RSJF forms, because they set empty fields to `undefined`.
  // This will cause issues with anyOf fields because they will be set to the first item on empty.
  const { inputFormData, outputFormData } = useMemo(
    () => ({
      inputFormData: camelToSnakeDeep(formData.inputs) || {},
      outputFormData: camelToSnakeDeep(formData.outputs) || {},
    }),
    [formData.inputs, formData.outputs]
  );

  const setInputFormData = useCallback(
    (values: GenericObjectType) => {
      setFormData((prev) => ({ ...prev, inputs: snakeToCamelDeep(values) } as ProcessRunFormData));
    },
    [setFormData]
  );

  const setOutputFormData = useCallback(
    (values: GenericObjectType) => {
      setFormData((prev) => ({ ...prev, outputs: snakeToCamelDeep(values) } as ProcessRunFormData));
    },
    [setFormData]
  );

  // ========================================
  // 4. DEFAULT VALUE AND JOB RESULT INITIALIZATION
  // ========================================
  // Apply schema defaults to input/output fields when they load. We do this because:
  // Defaults are either applied 1 by 1 or not at all in some of our widgets (dataset selector)
  // So we just apply the defaults in 1 go and don't rely on the widgets to do it
  // We useRunOnceWhen instead of useEffect because it runs only once and doesn't wait for the next render cycle.
  // But we can't do it immediately because inputSchema sometimes needs to fetch first, so we wait for it to be available before applying the defaults.

  useRunOnceWhen(!!inputSchema, () => {
    if (!inputSchema) throw new Error("Never happens");

    // Only apply if there is no job, otherwise we would overwrite the job outputs.
    if (hasJob) return;

    const schemaDefaults = getSchemaDefaultValues(inputSchema);
    if (Object.keys(schemaDefaults).length) {
      setInputFormData(schemaDefaults);
    }
  });

  useRunOnceWhen(!!outputSchema, () => {
    if (!outputSchema) throw new Error("Never happens");

    // Only apply if there is no job, otherwise we would overwrite the job outputs.
    if (hasJob) return;

    const schemaDefaults = getSchemaDefaultValues(outputSchema);
    if (Object.keys(schemaDefaults).length) {
      setOutputFormData(schemaDefaults);
    }
  });

  const jobResultsQuery = useJobResultsQuery(sourceJob ?? job);

  useRunOnceWhen(jobResultsQuery.isSuccess, () => {
    setOutputFormData(jobResultsQuery.data ?? {});
  });

  // ========================================
  // 5. PROCESS EXECUTION
  // ========================================
  // Handle submitting the form and creating a new job run.
  const startProcess = async () => {
    try {
      setIsCreating(true);
      const run = await startRun({
        project,
        process,
        values: {
          name: nameFormData.name,
          inputs: inputFormData,
          outputs: outputFormData,
        },
        workflowId,
        processCompleteMessage: intl.formatMessage(
          {
            id: "process.toast.completed.description",
            defaultMessage: `Done with process: {name}. You can now view the results in 'Analysis tools'.`,
          },
          { name: nameFormData.name }
        ),
      });
      setJob(run?.job, { dontSaveInHistory: true });
    } finally {
      setIsCreating(false);
    }
  };

  // ========================================
  // 6. FORM VALIDATION
  // ========================================
  // Check that all required fields are present and valid according to their schemas.

  const formIsValid = useMemo(() => {
    if (!inputSchema || !outputSchema) return false;
    return (
      customValidator.isValid(nameSchema, nameFormData, nameSchema) &&
      customValidator.isValid(inputSchema, inputFormData, inputSchema) &&
      customValidator.isValid(outputSchema, outputFormData, outputSchema) &&
      checkRequiredForSchema(nameSchema, nameFormData) &&
      checkRequiredForSchema(inputSchema, inputFormData) &&
      checkRequiredForSchema(outputSchema, outputFormData)
    );
  }, [nameSchema, inputFormData, inputSchema, nameFormData, outputFormData, outputSchema]);

  // ========================================
  // 7. RETURN HOOK RESULT
  // ========================================

  return {
    isLoading: isLoading || jobResultsQuery.isLoading || jobQuery.isLoading,
    nameSchema,
    nameFormData,
    setNameFormData,
    inputSchema,
    setInputFormData,
    outputSchema,
    setOutputFormData,
    errors,
    formIsValid,
    startProcess,
    outputFormData,
    inputFormData,
  };
}
